import { Link } from '@/components/link/Link';
import { getFutureSchedulesByTellerId } from '@raysee/firestore';
import { PhoneIcon } from '@raysee/icon';
import { Teller } from '@raysee/schema';
import { removeDecorationTags } from '@raysee/utility';
import dayjs from 'dayjs';
import Image from 'next/legacy/image';
import { useMemo } from 'react';
import useSWR from 'swr';

export default function DebutPanel({ teller }: { teller: Teller }) {
  const { data: schedules } = useSWR(
    ['schedules/future', teller.id],
    () => getFutureSchedulesByTellerId(teller.id),
    {
      revalidateOnMount: true,
      revalidateOnFocus: false,
    },
  );

  const waitingText = useMemo(() => {
    if (schedules?.length > 0) {
      const nearySchedule = schedules.sort(
        (a, b) => a.startTime.getDate() - b.startTime.getDate(),
      )[0];
      const startDate = dayjs(nearySchedule.startTime);
      const endDate = dayjs(nearySchedule.endTime);

      return `待機予定 ${startDate.format('MM/DD')} ${startDate.format('H:mm')}～${endDate.format(
        'H:mm',
      )}`;
    }
  }, [schedules]);

  const description = teller.catchphrase || removeDecorationTags(teller.description);

  return (
    <div className="p-4 space-y-5">
      <p className="text-phone-primary font-serif font-bold text-center">{description}</p>
      <div className="flex space-x-5">
        <div>
          <Link href={`/tellers/${teller.id}`} legacyBehavior>
            <div
              className="border-[#483B43] m-auto rounded-full border-[3px]"
              style={{ width: 120, height: 120 }}
            >
              <Image
                src={teller.imageUrl ?? 'https://via.placeholder.com/200'}
                alt={teller.name}
                className="rounded-full"
                width={120}
                height={120}
              />
            </div>
          </Link>
        </div>

        <div className="space-y-2">
          <div className="flex items-center">
            <p className="font-bold text-sm">
              <span className="text-xl">{teller.name}</span>先生
            </p>
          </div>

          <div className="flex">
            <div className="flex min-w-[100px] items-center gap-1">
              <div className="flex h-4 w-4 items-center justify-center rounded-sm bg-[#6B6B6B]">
                <PhoneIcon width={12} height={12} />
              </div>
              <p className="text-xs font-semibold">
                {teller.minPrice?.phone
                  ? `${new Intl.NumberFormat('ja-JP').format(teller.minPrice.phone)}円 / 分`
                  : ''}
              </p>
            </div>
            {teller.experienceYear && (
              <div className="flex items-center gap-1">
                <p className="text-xs font-semibold">鑑定歴: {teller.experienceYear}年</p>
              </div>
            )}
          </div>

          <div>
            <ul className="flex flex-wrap gap-1">
              {teller.categories.map((category, i) => (
                <li key={i}>
                  <a className="bg-[#ed70c0] rounded-full px-2 text-xs text-white">{category}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="flex justify-center">
        <Link href={`/tellers/${teller.id}`} legacyBehavior>
          <button
            type="button"
            className="bg-[#a866c9] border-2 border-[#fee990] text-sm text-white font-bold mx-10 py-3 px-10 rounded-full inline-flex items-center justify-center flex-1"
          >
            詳細はこちら
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256">
              <title>more</title>
              <path
                fill="currentColor"
                d="m184.49 136.49l-80 80a12 12 0 0 1-17-17L159 128L87.51 56.49a12 12 0 1 1 17-17l80 80a12 12 0 0 1-.02 17"
              />
            </svg>
          </button>
        </Link>
      </div>

      {waitingText && (
        <div className="flex items-center justify-center text-gray-400 text-sm font-bold space-x-1 ">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8s8 3.58 8 8s-3.58 8-8 8m.5-13H11v6l5.25 3.15l.75-1.23l-4.5-2.67z"
            />
          </svg>
          <span>{waitingText}</span>
        </div>
      )}
    </div>
  );
}
